import React from "react";
import { Plus } from "lucide-react";

const PLUS_CIRCLE =
  "inline-flex shrink-0 items-center justify-center rounded-full border border-white bg-gray-700 text-white";

/**
 * Click-to-insert slot. Two variants:
 * - `variant="between"` (default): a 1px white line on hover/focus or while inserting.
 * - `variant="empty"`: dashed drop zone; plain white border when inserting.
 */
export default function SlotButton({
  onClick,
  variant = "between",
  label = "Add step",
  /** True while the insert popover is open for this slot. */
  highlightInsert = false,
}) {
  const stop = (e) => e.stopPropagation();

  if (variant === "empty") {
    return (
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          onClick(e);
        }}
        className={`group relative flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-[11px] transition-colors focus:outline-none ${
          highlightInsert
            ? "border-white bg-white/5 text-white"
            : "border-gray-700 bg-gray-900/30 panel-text-helper hover:border-white/60 hover:bg-white/5 hover:text-gray-200 focus-visible:border-white focus-visible:text-gray-100"
        }`}
        aria-label={label}
        aria-expanded={highlightInsert}
      >
        <span className={`${PLUS_CIRCLE} h-7 w-7`}>
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
        </span>
        <span>{label}</span>
      </button>
    );
  }

  const lineVisible = "h-px bg-white";
  const lineHidden = "h-px bg-transparent group-hover:bg-white group-focus-visible:bg-white";

  const plusVisibility = highlightInsert
    ? "opacity-100"
    : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100";

  return (
    <button
      type="button"
      onClick={(e) => {
        stop(e);
        onClick(e);
      }}
      className="group relative flex h-5 w-full items-center justify-center focus:outline-none"
      aria-label={label}
      aria-expanded={highlightInsert}
    >
      <span
        className={`absolute inset-x-2 top-1/2 -translate-y-1/2 rounded-full transition-colors duration-150 ${
          highlightInsert ? lineVisible : lineHidden
        }`}
      />
      <span
        className={`relative z-10 ${PLUS_CIRCLE} h-5 w-5 transition-opacity duration-150 ${plusVisibility}`}
      >
        <Plus className="h-3 w-3" strokeWidth={2.25} aria-hidden="true" />
      </span>
    </button>
  );
}
